import { defineStore } from "pinia";
import { ref } from "vue";
import { useToast } from "vue-toastification";
import agentService from "@/services/agent";

export const useAgentsStore = defineStore("agents", () => {
  const toast = useToast();
  const loading = ref(false);

  async function fetchAgentsGrid(companyId) {
    loading.value = true;
    try {
      const res = await agentService.getAgentsGrid(companyId);
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function fetchAgentsHaveStores(companyId) {
    loading.value = true;
    try {
      const res = await agentService.getAgentsHaveStores(companyId);
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function fetchAgentsSimple(companyId) {
    loading.value = true;
    try {
      const res = await agentService.getAgentsSimple(companyId);
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function fetchAgentTypes() {
    loading.value = true;
    try {
      const res = await agentService.getAgentTypes();
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function fetchAgentById(payload) {
    loading.value = true;
    try {
      const res = await agentService.getAgentById(payload);
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function addAgent(payload) {
    loading.value = true;
    try {
      const res = await agentService.addAgent(payload);
      toast.success(res.message);
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function duplicateAgent(payload) {
    loading.value = true;
    try {
      const res = await agentService.duplicateAgent(payload);
      toast.success(res.message);
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function editAgent(payload) {
    loading.value = true;
    try {
      const res = await agentService.editAgent(payload);
      toast.success(res.message);
      return res;
    } catch (err) {
      if (err?.status === 409) {
        toast.warning("This agent changed elsewhere. Reloading the latest version.");
      } else {
        toast.error(err?.data?.message);
      }
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function deleteAgent(payload) {
    loading.value = true;
    try {
      const res = await agentService.deleteAgent(payload);
      toast.success(res.message);
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function runAgentTest(payload) {
    loading.value = true;
    try {
      const res = await agentService.runAgentTest(payload);
      toast.success(res.message);
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function fetchAgentVoices() {
    loading.value = true;
    try {
      const res = await agentService.getAgentVoices();
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function fetchAvatars() {
    loading.value = true;
    try {
      const res = await agentService.getAvatars();
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function fetchLlmModels() {
    loading.value = true;
    try {
      const res = await agentService.getLlmModels();
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function fetchSttModels() {
    loading.value = true;
    try {
      const res = await agentService.getSttModels();
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function fetchAgentVoiceById(payload) {
    loading.value = true;
    try {
      const res = await agentService.getAgentVoiceById(payload);
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function fetchDefaultCompanyPrompt(companyId) {
    loading.value = true;
    try {
      const res = await agentService.getDefaultCompanyPrompt(companyId);
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function fetchPromptHistory(payload) {
    loading.value = true;
    try {
      const res = await agentService.getPromptHistory(payload);
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function assignPrompt(payload) {
    loading.value = true;
    try {
      const res = await agentService.assignPrompt(payload);
      toast.success(res.message);
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function fetchDocumentsByAgent(companyId, agentId) {
    loading.value = true;
    try {
      const res = await agentService.fetchDocumentsByAgent(companyId, agentId);
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function deleteDocumentByAgent(companyId, fileId) {
    loading.value = true;
    try {
      const res = await agentService.deleteDocumentByAgent(companyId, fileId);
      toast.success(res?.message || "Document deleted");
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function fetchAgentImages(companyId, agentId) {
    loading.value = true;
    try {
      const res = await agentService.getAgentImages(companyId, agentId);
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function deleteAgentImage(companyId, agentId, imageId) {
    loading.value = true;
    try {
      const res = await agentService.deleteAgentImage(companyId, agentId, imageId);
      toast.success(res?.message || "Image deleted");
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  return {
    loading,
    fetchAgentsGrid,
    fetchAgentsHaveStores,
    fetchAgentsSimple,
    fetchAgentTypes,
    fetchAgentById,
    addAgent,
    duplicateAgent,
    editAgent,
    deleteAgent,
    runAgentTest,
    fetchAgentVoices,
    fetchAvatars,
    fetchLlmModels,
    fetchSttModels,
    fetchAgentVoiceById,
    fetchDefaultCompanyPrompt,
    fetchPromptHistory,
    assignPrompt,
    fetchDocumentsByAgent,
    deleteDocumentByAgent,
    fetchAgentImages,
    deleteAgentImage,
  };
});
